<template>
  <div
    class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
  >
    <div
      v-for="(product, idx) in shown"
      :key="product.produktId"
      class="relative bg-branco shadow-sm rounded overflow-hidden hover:-translate-y-1 transition-transform cursor-pointer flex flex-col focus:outline-none focus:ring-2 focus:ring-azul-profundo"
      role="button"
      tabindex="0"
      @click="$emit('open', product)"
      @keydown.enter="$emit('open', product)"
      @keydown.space.prevent="$emit('open', product)"
    >
      <!-- selo de desconto -->
      <div
        v-if="
          product.status === 'Verkauf-ZumVerkauf' && discountOf(product).pct > 0
        "
        class="absolute left-2 top-2 z-10 px-2 py-0.5 rounded bg-vermelho-profundo text-branco text-xs font-bold shadow"
      >
        -{{ discountOf(product).pct }}%
      </div>

      <!-- ribbon 'Em breve na loja' -->
      <div
        v-if="product.status === 'Lagerbestand-Verfügbar'"
        class="absolute right-[-40px] top-3 z-10 rotate-45 bg-verde-profundo text-branco text-xs font-semibold pl-14 pr-7 py-1 shadow"
      >
        {{ $t("vitrine.grid.soon") }}
      </div>

      <div class="w-full aspect-square bg-cinza-200">
        <img
          :src="(product.images && product.images[0]) || placeholderImage"
          :alt="
            psImageAlt
              ? psImageAlt(product, 0)
              : product.name || $t('vitrine.grid.productImage')
          "
          class="w-full h-full object-contain"
          loading="lazy"
          decoding="async"
          :fetchpriority="imgPriority(idx)"
        />
      </div>

      <div class="p-4 flex flex-col flex-grow">
        <h3 class="font-semibold truncate">{{ product.name }}</h3>
        <div class="flex flex-col">
          <div
            class="flex items-center gap-1 text-sm text-texto-secundario cursor-pointer focus:outline-none focus:ring-2 focus:ring-azul-profundo rounded"
            role="button"
            tabindex="0"
            @click.stop="copyToClipboard(product.produktId)"
            @keydown.enter.stop="copyToClipboard(product.produktId)"
            @keydown.space.prevent.stop="copyToClipboard(product.produktId)"
          >
            <span class="hover:underline">{{ product.produktId }}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 text-cinza-500 hover:text-azul-profundo"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0
       012-2h8a2 2 0 012 2v2m-6
       12h8a2 2 0 002-2v-8a2 2 0
       00-2-2h-8a2 2 0 00-2 2v8a2
       2 0 002 2z"
              />
            </svg>
          </div>
          <transition name="fade">
            <span
              v-if="copiedId === product.produktId"
              class="text-xs text-verde-profundo mt-1"
              aria-live="polite"
            >
              {{ $t("vitrine.grid.copied") }}
            </span>
          </transition>
        </div>

        <!-- preços -->
        <div class="mt-1 min-h-[3.25rem]">
          <template v-if="product.status === 'Verkauf-ZumVerkauf'">
            <template v-if="discountOf(product).pct > 0">
              <div class="text-sm text-cinza-500 line-through">
                {{ formatEuro(product.preis) }} €
              </div>
              <div class="text-lg font-bold text-vermelho-profundo">
                <span
                  v-if="product.preisAb"
                  class="text-xs font-normal text-texto-secundario mr-1"
                >
                  {{ $t("vitrine.grid.from") }}
                </span>
                {{ finalPriceOf(product) }} €
              </div>
            </template>
            <template v-else>
              <div class="text-lg font-bold text-cinza-900">
                <span
                  v-if="product.preisAb"
                  class="text-xs font-normal text-texto-secundario mr-1"
                >
                  {{ $t("vitrine.grid.from") }}
                </span>
                {{ formatEuro(product.preis) }} €
              </div>
            </template>
          </template>
        </div>

        <!-- janela e countdown -->
        <div class="mt-2 mb-3 space-y-1 min-h-[2.25rem]">
          <template
            v-if="
              product.status === 'Verkauf-ZumVerkauf' &&
              discountOf(product).pct > 0
            "
          >
            <div class="text-[11px] text-texto-secundario">
              {{ dateRangeOf(product).start }} –
              {{ dateRangeOf(product).end }}
            </div>
            <div class="text-[12px] font-medium text-verde-suave">
              {{ $t("vitrine.grid.endsIn") }}:
              {{ countdownTo(discountOf(product).endDate) }}
            </div>
          </template>
        </div>

        <!-- spacer flex: garante botão no rodapé do card -->
        <div class="mt-auto"></div>

        <button
          type="button"
          class="mt-3 bg-azul-profundo text-branco px-4 py-2 rounded hover:bg-verde-suave focus:outline-none focus:ring-2 focus:ring-azul-profundo"
        >
          {{ $t("vitrine.grid.seeDetails") }}
        </button>
      </div>
    </div>

    <!-- Sentinel para paginação infinita (apenas quando não há 'limit') -->
    <div
      v-if="useInfinite && hasMore"
      ref="sentinel"
      class="col-span-full h-1"
      aria-hidden="true"
    ></div>
  </div>
</template>

<script>
export default {
  name: "VitrineProductGrid",
  inject: ["psImageAlt"],
  props: {
    products: { type: Array, default: () => [] },
    placeholderImage: { type: String, required: true },
    campaignsCat: { type: Array, default: () => [] },
    campaignsProd: { type: Array, default: () => [] },
    limit: { type: Number, default: null },
  },
  emits: ["open"],
  data() {
    return {
      now: Date.now(),
      tick: null,
      copiedId: null,

      // Paginação infinita simples
      BATCH_INITIAL: 40,
      BATCH_SIZE: 40,
      itemsToShow: 40,
      io: null,
    };
  },
  computed: {
    useInfinite() {
      // só ativa scroll infinito quando NÃO há 'limit'
      return this.limit == null;
    },
    hasMore() {
      return this.useInfinite && this.itemsToShow < this.products.length;
    },
    shown() {
      // quando 'limit' existir (ex.: seções iniciais), preserva o comportamento atual
      if (this.limit != null) {
        return this.products.slice(0, this.limit);
      }
      // grid completo (aba de navegação): paginação por lotes
      return this.products.slice(0, this.itemsToShow);
    },
  },
  mounted() {
    // timer já existente
    if (this.tick) clearInterval(this.tick);
    this.tick = setInterval(() => {
      this.now = Date.now();
    }, 1000);

    // inicia paginação infinita
    if (this.useInfinite) {
      this.itemsToShow = this.BATCH_INITIAL;
      this.$nextTick(() => this.setupObserver());
    }
  },
  beforeUnmount() {
    if (this.tick) clearInterval(this.tick);
    this.teardownObserver();
  },
  watch: {
    // ao trocar o conjunto (filtros/ordenação), reinicia a janela
    products() {
      if (this.useInfinite) {
        this.itemsToShow = this.BATCH_INITIAL;
        this.$nextTick(() => {
          this.teardownObserver();
          this.setupObserver();
        });
      }
    },
    // se 'limit' for alterado dinamicamente, ajusta observador
    limit() {
      this.$nextTick(() => {
        this.teardownObserver();
        if (this.useInfinite) this.setupObserver();
      });
    },
  },
  methods: {
    // ---- prioridade do fetch: apenas a primeira imagem visível "acima da dobra"
    imgPriority(localIndex) {
      // No contexto do 'shown', o primeiro card recebe 'high'
      return localIndex === 0 ? "high" : "low";
    },

    // ---- clipboard existente
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.copiedId = text;
        setTimeout(() => {
          if (this.copiedId === text) this.copiedId = null;
        }, 2000);
      } catch (err) {
        console.error("Erro ao copiar:", err);
      }
    },

    // ---- IntersectionObserver para o sentinel (carrega mais blocos)
    setupObserver() {
      if (!this.useInfinite) return;
      const el = this.$refs.sentinel;
      if (!el) return;

      // rootMargin grande para pré-carregar antes de chegar no rodapé
      this.io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry && entry.isIntersecting) {
            this.itemsToShow = Math.min(
              this.itemsToShow + this.BATCH_SIZE,
              this.products.length
            );
            // se ainda houver mais, mantém o sentinel; caso contrário, encerra
            if (!this.hasMore) {
              this.teardownObserver();
            }
          }
        },
        { root: null, threshold: 0, rootMargin: "800px 0px" }
      );

      this.io.observe(el);
    },
    teardownObserver() {
      if (this.io) {
        try {
          this.io.disconnect();
        } catch (e) {
          /* noop */
        }
        this.io = null;
      }
    },

    // ---- helpers de data/percentual (originais)
    toNum(euro) {
      if (typeof euro === "number") return euro;
      const n = Number(
        String(euro)
          .replace(/[^0-9,.]/g, "")
          .replace(",", ".")
      );
      return Number.isFinite(n) ? n : 0;
    },
    formatEuro(val) {
      const n = this.toNum(val);
      return n.toFixed(2).replace(".", ",");
    },
    endOfDay(iso) {
      const s = String(iso || "").slice(0, 10);
      const [y, m, d] = s.split("-").map((n) => parseInt(n, 10));
      const dt = new Date(y || 1970, (m || 1) - 1, d || 1);
      dt.setHours(23, 59, 59, 999); // local time
      return dt.getTime();
    },
    withinWindow(startIso, endIso) {
      const start = this.endOfDay(startIso) - 24 * 60 * 60 * 1000 + 1000; // começo do dia
      const end = this.endOfDay(endIso);
      return this.now >= start && this.now <= end;
    },

    // ---- cálculo do melhor desconto para um produto (original)
    discountOf(product) {
      const grp = product?.klassifizierung?.gruppeId || "";
      const kat = product?.klassifizierung?.kategorieId || "";
      const pid = product?.produktId || "";

      const valid = (x) =>
        x &&
        x.status !== "cancelada" &&
        this.withinWindow(x.startDatum, x.endDatum);

      const prodList = (this.campaignsProd || []).filter(
        (x) => x.produktId === pid && valid(x)
      );
      const catList = (this.campaignsCat || []).filter(
        (x) => x.gruppeId === grp && x.kategorieId === kat && valid(x)
      );

      const pickMax = (arr) =>
        arr.length
          ? arr.slice().sort((a, b) => (b.prozent || 0) - (a.prozent || 0))[0]
          : null;

      const pMax = pickMax(prodList);
      const cMax = pickMax(catList);

      let chosen = null;
      if (pMax && cMax) {
        if ((cMax.prozent || 0) > (pMax.prozent || 0)) {
          chosen = { ...cMax, src: "cat" };
        } else if ((pMax.prozent || 0) > (cMax.prozent || 0)) {
          chosen = { ...pMax, src: "prod" };
        } else {
          chosen = { ...cMax, src: "cat" };
        }
      } else if (pMax) {
        chosen = { ...pMax, src: "prod" };
      } else if (cMax) {
        chosen = { ...cMax, src: "cat" };
      }

      if (!chosen) return { pct: 0, endDate: null };
      return {
        pct: Math.abs(Number(chosen.prozent || 0)),
        endDate: this.endOfDay(chosen.endDatum),
      };
    },

    finalPriceOf(product) {
      const pct = this.discountOf(product).pct;
      const base = this.toNum(product.preis);
      const final = Math.round(base * (1 - pct / 100) * 100) / 100;
      return this.formatEuro(final);
    },

    dateRangeOf(product) {
      const d = this.discountOf(product);
      if (!d.pct) return { start: "", end: "" };
      // mostramos as datas originais em DD.MM.YYYY
      const fmt = (iso) => {
        const s = String(iso || "").slice(0, 10);
        const [y, m, d] = s.split("-");
        return `${d}.${m}.${y}`;
      };
      const src = this._findSrc(product, d.pct); // pega o registro usado
      return { start: fmt(src?.startDatum), end: fmt(src?.endDatum) };
    },

    _findSrc(product, pct) {
      const pid = product?.produktId || "";
      const grp = product?.klassifizierung?.gruppeId || "";
      const kat = product?.klassifizierung?.kategorieId || "";
      const valid = (x) =>
        x &&
        x.status !== "cancelada" &&
        this.withinWindow(x.startDatum, x.endDatum) &&
        Number(x.prozent || 0) === pct;

      const prod = (this.campaignsProd || []).find(
        (x) => x.produktId === pid && valid(x)
      );
      const cat = (this.campaignsCat || []).find(
        (x) => x.gruppeId === grp && x.kategorieId === kat && valid(x)
      );
      return cat || prod || null;
    },

    countdownTo(endMs) {
      if (!endMs) return "—";
      const pad = (n) => String(n).padStart(2, "0");
      const diff = Math.max(0, endMs - this.now);
      const total = Math.floor(diff / 1000);
      const d = Math.floor(total / 86400);
      const h = Math.floor((total % 86400) / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      if (d > 0) return `${d}d ${pad(h)}h ${pad(m)}m ${pad(s)}s`;
      return `${pad(h)}h ${pad(m)}m ${pad(s)}s`;
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}
</style>
